import { useState } from 'react';

import { Edit as EditIcon } from '@mui/icons-material';
import { Box, FormControlLabel, IconButton, Skeleton, Stack, Switch, TextField, Typography } from '@mui/material';

import { themeColors } from 'colors';

const INTRO_TEXT = 'Define what this role can access and manage across modules.';
const ROLE_NAME_LABEL = 'role name';
const ROLE_DESCRIPTION_LABEL = 'role description';
const ROLE_NAME_PLACEHOLDER = 'Add Role Name';
const ROLE_DESCRIPTION_PLACEHOLDER = 'Add Role Description';
const ROLE_NAME_MIN_LENGTH_HELPER = 'Role name must be at least 3 characters';
const SHOW_ENABLED_ONLY_LABEL = 'Show enabled only';

const roleNameLabelSx = {
    color: themeColors.roleBuilder.roleName,
    fontWeight: 400,
} as const;

const roleDetailsRootStackSx = { width: '100%' } as const;

const roleDescriptionSkeletonStackSx = { gap: 0.5 } as const;

const showEnabledOnlyFormControlLabelSx = { ml: 0, alignItems: 'center' } as const;

/** MUI helper text default margin is large; keep error snug under the name field. */
const roleNameFieldFormHelperTextProps = { sx: { m: 0, mt: 0.25, lineHeight: 1.33 } } as const;

const displayPlaceholderTypographySx = { color: 'text.secondary' } as const;

/** Long names wrap instead of overflowing into the permissions column (flex child must shrink). */
const roleNameDisplayTypographySx = {
    minWidth: 0,
    flex: '1 1 auto',
    maxWidth: '100%',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
} as const;

/** Role name + role description read-only rows: pencil sits next to the text (same as each other, not flush right). */
const roleFieldDisplayRowSx = {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: 1,
    rowGap: 1,
} as const;

export type RoleDetailsProps = {
    name: string;
    description: string;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    /** When true, name field shows error + helper (non-empty but under min length). */
    showRoleNameLengthError?: boolean;
    showEnabledOnly: boolean;
    onShowEnabledOnlyChange: (value: boolean) => void;
    readOnly: boolean;
    /** Role record is still loading (view/edit with id); show skeletons for name and description. */
    isLoading?: boolean;
};

const RoleDetails = ({
    name,
    description,
    onNameChange,
    onDescriptionChange,
    showRoleNameLengthError = false,
    showEnabledOnly,
    onShowEnabledOnlyChange,
    readOnly,
    isLoading = false,
}: RoleDetailsProps) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);

    return (
        <Stack spacing={2} data-testid="access-center-role-details" sx={roleDetailsRootStackSx} aria-busy={isLoading}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {INTRO_TEXT}
            </Typography>

            <Stack spacing={1}>
                <Typography variant="body2" sx={roleNameLabelSx}>
                    {ROLE_NAME_LABEL}
                </Typography>
                {isLoading ? (
                    <Skeleton
                        variant="text"
                        height={28}
                        width="85%"
                        data-testid="access-center-role-details-name-skeleton"
                    />
                ) : readOnly || !isEditingName ? (
                    <Stack sx={roleFieldDisplayRowSx}>
                        <Typography
                            variant="body1"
                            data-testid="access-center-role-details-name-display"
                            sx={
                                name.trim()
                                    ? roleNameDisplayTypographySx
                                    : { ...roleNameDisplayTypographySx, ...displayPlaceholderTypographySx }
                            }
                        >
                            {name.trim() ? name : ROLE_NAME_PLACEHOLDER}
                        </Typography>
                        {!readOnly && (
                            <IconButton
                                size="small"
                                aria-label="Edit role name"
                                onClick={() => setIsEditingName(true)}
                                data-testid="access-center-role-details-edit-name"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                ) : (
                    <TextField
                        value={name}
                        onChange={(e) => onNameChange(e.target.value)}
                        onBlur={() => setIsEditingName(false)}
                        placeholder={ROLE_NAME_PLACEHOLDER}
                        variant="outlined"
                        fullWidth
                        size="small"
                        margin="dense"
                        autoFocus
                        error={showRoleNameLengthError}
                        helperText={showRoleNameLengthError ? ROLE_NAME_MIN_LENGTH_HELPER : undefined}
                        FormHelperTextProps={roleNameFieldFormHelperTextProps}
                        inputProps={{ 'data-testid': 'access-center-create-role-name-input' }}
                    />
                )}
            </Stack>

            <Stack spacing={1}>
                <Typography variant="body2" sx={roleNameLabelSx}>
                    {ROLE_DESCRIPTION_LABEL}
                </Typography>
                {isLoading ? (
                    <Stack
                        sx={roleDescriptionSkeletonStackSx}
                        data-testid="access-center-role-details-description-skeleton"
                    >
                        <Skeleton variant="text" width="100%" />
                        <Skeleton variant="text" width="78%" />
                    </Stack>
                ) : readOnly || !isEditingDescription ? (
                    <Stack sx={roleFieldDisplayRowSx}>
                        <Typography
                            variant="body1"
                            data-testid="access-center-role-details-description-display"
                            sx={(description ?? '').trim() ? undefined : displayPlaceholderTypographySx}
                        >
                            {(description ?? '').trim() ? (description ?? '').trim() : ROLE_DESCRIPTION_PLACEHOLDER}
                        </Typography>
                        {!readOnly && (
                            <IconButton
                                size="small"
                                aria-label="Edit role description"
                                onClick={() => setIsEditingDescription(true)}
                                data-testid="access-center-role-details-edit-description"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                ) : (
                    <TextField
                        value={description}
                        onChange={(e) => onDescriptionChange(e.target.value)}
                        onBlur={() => setIsEditingDescription(false)}
                        placeholder={ROLE_DESCRIPTION_PLACEHOLDER}
                        variant="outlined"
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        autoFocus
                        inputProps={{ 'data-testid': 'access-center-create-role-description-input' }}
                    />
                )}
            </Stack>

            <Box>
                <FormControlLabel
                    sx={showEnabledOnlyFormControlLabelSx}
                    control={
                        <Switch
                            color="secondary"
                            checked={showEnabledOnly}
                            onChange={(_, v) => onShowEnabledOnlyChange(v)}
                            disabled={isLoading}
                            data-testid="access-center-role-details-show-enabled-only"
                        />
                    }
                    label={
                        <Typography variant="body1" component="span">
                            {SHOW_ENABLED_ONLY_LABEL}
                        </Typography>
                    }
                    labelPlacement="end"
                />
            </Box>
        </Stack>
    );
};

export default RoleDetails;
